import { useState } from 'react'

import { malus, ru } from '../../lib/physics'
import { Predict, Slider, useFrames } from './common'

/**
 * Два поляроида (урок 1): свет проходит через два одинаковых серых фильтра. Повернул второй —
 * стало темно, хотя оба фильтра прозрачные. Доля прошедшего света — cos² угла между ними.
 */

export default function PolarizerLab() {
  const [угол, setУгол] = useState(0)
  const [крутить, setКрутить] = useState(false)

  useFrames((dt) => {
    if (крутить) setУгол((a) => (a + dt * 30) % 180)
  })

  const доля = malus(угол)
  // экран: свет после первого фильтра — половина лампы, после второго — ещё доля cos²
  const яркость = 0.5 * доля
  const фильтр = (cx: number, поворот: number, подпись: string) => (
    <g>
      <circle cx={cx} cy={110} r={44} className="fill-polar ink" strokeWidth={1.5} />
      <g transform={`rotate(${поворот} ${cx} 110)`}>
        {[-30, -20, -10, 0, 10, 20, 30].map((d) => (
          <line key={d} x1={cx + d} y1={110 - Math.sqrt(44 * 44 - d * d) + 2} x2={cx + d} y2={110 + Math.sqrt(44 * 44 - d * d) - 2} className="ink-muted" strokeWidth={1} />
        ))}
      </g>
      <text x={cx} y={176} textAnchor="middle" className="lab-tick">{подпись}</text>
    </g>
  )

  return (
    <div className="card">
      <svg className="lab-svg" viewBox="0 0 360 190" role="img" aria-label="Свет через два поляроида">
        <circle cx={22} cy={110} r={14} className="fill-flame" />
        <text x={22} y={176} textAnchor="middle" className="lab-tick">лампа</text>
        <rect x={36} y={100} width={56} height={20} className="fill-light" opacity={1} />
        {фильтр(110, 0, 'первый')}
        <rect x={154} y={100} width={44} height={20} className="fill-light" opacity={0.5} />
        {фильтр(216, угол, `второй: ${Math.round(угол)}°`)}
        <rect x={260} y={100} width={46} height={20} className="fill-light" opacity={яркость} />
        <rect x={306} y={60} width={40} height={100} rx={3} className="ink" fill={`rgba(255, 236, 160, ${яркость * 2})`} strokeWidth={1.5} />
        <text x={326} y={176} textAnchor="middle" className="lab-tick">экран</text>
      </svg>

      <div className="lab-big">
        На экран проходит: <b>{ru(доля * 100, 0)}%</b> света после первого фильтра
      </div>
      <div className="lab-caption">
        {угол % 180 > 80 && угол % 180 < 100
          ? 'Фильтры скрещены — свет не проходит, хотя каждый фильтр по отдельности прозрачный.'
          : 'Чем больше угол между фильтрами (до 90°), тем меньше света проходит.'}
      </div>

      <Predict
        question="Что будет, если повернуть второй фильтр на 90°?"
        options={['ничего не изменится', 'станет темно', 'станет ярче']}
        right={1}
        revealed={Math.abs((угол % 180) - 90) < 6}
        resetKey={крутить ? 'auto' : 'hand'}
        explain="Поляроид пропускает свет, колеблющийся только в одном направлении. Скрещённые поляроиды гасят свет друг друга — физика позволяет «увидеть невидимое», например напряжения внутри прозрачного угольника."
      />

      <Slider label="Угол второго фильтра" value={Math.round(угол)} min={0} max={180} step={1} unit="°" onChange={(v) => { setКрутить(false); setУгол(v) }} />
      <div className="row-wrap" style={{ marginTop: 10 }}>
        <button className={крутить ? 'btn btn-primary' : 'btn'} onClick={() => setКрутить((x) => !x)}>
          {крутить ? '⏸ Остановить' : '🔄 Вращать фильтр'}
        </button>
      </div>
      <p className="muted small">
        Это световое явление подробно изучают в 11 классе. Экран телефона и компьютера тоже закрыт
        поляроидом — проверь, повернув перед ним солнечные очки с поляризацией.
      </p>
    </div>
  )
}
